// The nudges are all hard to reach on purpose: the cap offer needs a
// configured dashboard usagePromo plus enough real weekly caps, the rating
// banner needs Pro, a trial, or hours of listening, and the product updates
// banner needs an update to a new release line. That makes them nearly
// impossible to look at while building them.
//
// These overrides let the debug bar stand any of them up. They are applied
// where props are handed to the popup, never written back into state, so the
// real snapshot stays honest and no analytics or stored counters move.

import {
  PREVIEW_TOOL_URL,
  resolveUpdateBannerContent,
} from "./update-banner";
import { PROMO_NUDGE_ORDER } from "./promo-nudges";

// `nudge` holds one of the promo nudges' ids while that one is previewed,
// else false.
export const EMPTY_NUDGE_PREVIEW = {
  promo: false,
  rate: false,
  update: false,
  nudge: false,
};

// Stand-in for an unconfigured account, so the promo step can be seen before
// usagePromo is switched on in the dashboard.
export const PREVIEW_PROMO = {
  enabled: true,
  promoId: "promo_preview",
  afterCaps: 1,
  label: "50% off Pro",
};

export function resolveNudgePreview({
  preview,
  usagePromo,
  weeklyCapCount,
  ratePromptVisible,
  updateBannerVisible,
  updateBannerContent,
  promoNudge,
  version,
} = {}) {
  const showPromo = preview?.promo === true;
  const realCount = Number(weeklyCapCount) || 0;

  // Prefer the account's own offer so the preview shows the real copy; the
  // stand-in only fills in when nothing is configured yet.
  const promo = showPromo
    ? usagePromo?.label
      ? usagePromo
      : PREVIEW_PROMO
    : (usagePromo ?? null);

  const needed = showPromo ? Number(promo?.afterCaps) || 1 : 0;

  return {
    usagePromo: promo,
    weeklyCapCount: showPromo ? Math.max(realCount, needed) : realCount,
    ratePromptVisible: preview?.rate === true || ratePromptVisible === true,
    updateBannerVisible:
      preview?.update === true || updateBannerVisible === true,
    // A preview shows this release's own words even before the site serves
    // what they link to.
    updateBannerContent:
      updateBannerContent ??
      (preview?.update === true
        ? resolveUpdateBannerContent({ version, toolUrl: PREVIEW_TOOL_URL })
        : null),
    promoNudge:
      (PROMO_NUDGE_ORDER.includes(preview?.nudge) ? preview.nudge : null) ??
      promoNudge ??
      null,
  };
}

// `nudge` keeps a promo nudge's id; every other key is a plain on/off.
export function toggleNudgePreview(preview, key, value) {
  const next =
    key === "nudge"
      ? PROMO_NUDGE_ORDER.includes(value)
        ? value
        : false
      : value === true;
  return { ...EMPTY_NUDGE_PREVIEW, ...(preview || {}), [key]: next };
}

// The debug bar's one button for the promo nudges walks the rotation:
// off, youtube, feedback, review, share, off.
export function nextPromoNudgePreview(current) {
  const index = PROMO_NUDGE_ORDER.indexOf(current);
  if (index === -1) return PROMO_NUDGE_ORDER[0];
  return PROMO_NUDGE_ORDER[index + 1] ?? false;
}

// The in-page cap notice (popup/cap-notice.js) only appears once per cap
// period, after minutes actually run out. The debug bar stands it up on the
// current tab instead: weekly or monthly wording, and the discount variant
// whenever the Promo toggle is on (the account's own offer, or the stand-in).
// `preview` makes the notice count nothing and move no stored record, and
// tags it "Preview" on screen.
export function capNoticePreviewData({ period, preview, usagePromo } = {}) {
  const promo =
    preview?.promo === true
      ? usagePromo?.label
        ? usagePromo
        : PREVIEW_PROMO
      : null;
  return {
    period: period === "monthly" ? "monthly" : "weekly",
    promoLabel: promo?.label || null,
    promoId: promo?.promoId || null,
    preview: true,
  };
}

// What a previewed promo CTA will actually reach, spelled out in the debug
// bar: the stand-in has no real offer behind it, so checkout opens without a
// discount until the dashboard's usagePromo is configured.
export function promoPreviewNote({ preview, usagePromo } = {}) {
  if (preview?.promo !== true) return null;
  return usagePromo?.promoId
    ? `Preview: checkout carries ${usagePromo.promoId}`
    : "Preview: no usagePromo configured, checkout has no discount";
}
